import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { 
  LogOut, Settings, Save, Store, Mail, Phone, MapPin, Clock, Package,
  User, Lock, Eye, EyeOff, Users, Plus, Edit, Trash2, X, Shield,
  Palette, Upload, Image, Globe, Copy, CheckCircle, AlertCircle,
  ShoppingBag, Star, ArrowUp, ArrowDown, GripVertical, FileText, Zap
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ROLE_LABELS = {
  admin: 'Administrador',
  empleado: 'Empleado (Cajero)',
  mesera: 'Mesera',
  inventario: 'Inventario (Bodega)',
  cocina: 'Cocina'
};

const ROLE_COLORS = {
  admin: 'bg-purple-100 text-purple-700',
  empleado: 'bg-blue-100 text-blue-700',
  mesera: 'bg-pink-100 text-pink-700',
  inventario: 'bg-amber-100 text-amber-700',
  cocina: 'bg-orange-100 text-orange-700'
};

const StoreSettings = () => {
  const { storeDomain, adminPath } = useParams();
  const { user, logout, getAuthHeader, getAdminBasePath, hasPermission, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const basePath = adminPath ? `/${adminPath}` : (storeDomain ? `/admin/${storeDomain}` : getAdminBasePath());
  const [activeTab, setActiveTab] = useState('store');
  const [settings, setSettings] = useState({
    store_name: '',
    email: '',
    phone: '',
    address: '',
    schedule: '',
    timezone: 'America/Guayaquil'
  });
  const [credentials, setCredentials] = useState({
    newUsername: '',
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // User management state
  const [storeUsers, setStoreUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [userForm, setUserForm] = useState({ username: '', password: '', role: 'empleado' });
  const [showUserForm, setShowUserForm] = useState(false);
  const [isCreatingUser, setIsCreatingUser] = useState(false);
  const [showUserPassword, setShowUserPassword] = useState(false);
  const [deleteUserConfirm, setDeleteUserConfirm] = useState(null);
  const [usernameAvailable, setUsernameAvailable] = useState(null);
  const [checkingUsername, setCheckingUsername] = useState(false);
  const [credUsernameAvailable, setCredUsernameAvailable] = useState(null);
  const [checkingCredUsername, setCheckingCredUsername] = useState(false);

  // Customization state
  const [customization, setCustomization] = useState({
    store_name: '', logo_url: '', primary_color: '#16a34a', secondary_color: '#f0fdf4',
    text_color: '#1a1a2e', phone: '', address: '', schedule: '', owner_name: '',
    product_card_size: 'medium'
  });
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState('');
  const [loadingCustom, setLoadingCustom] = useState(false);

  // Storefront editor state
  const [storefrontData, setStorefrontData] = useState({ welcome_text: '', banner_url: '', featured_products: [], product_order: [], product_card_size: 'medium', store_subtitle: '', announcement: '', whatsapp: '', instagram: '', facebook: '', show_prices: true, layout_style: 'grid' });
  const [bannerFile, setBannerFile] = useState(null);
  const [bannerPreview, setBannerPreview] = useState('');
  const [storeProducts, setStoreProducts] = useState([]);
  const [loadingStorefront, setLoadingStorefront] = useState(false);
  const [savingStorefront, setSavingStorefront] = useState(false);

  const loadSettings = useCallback(async () => {
    try {
      const storeId = user?.store_id || localStorage.getItem('admin_store_id');
      const response = await axios.get(`${API}/settings${storeId ? `?store_id=${storeId}` : ''}`, { headers: getAuthHeader() });
      setSettings(response.data);
    } catch (error) {
      toast.error('Error al cargar configuración');
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.store_id]);

  const loadStoreUsers = useCallback(async () => {
    setLoadingUsers(true);
    try {
      const response = await axios.get(`${API}/admin/store-users`, { headers: getAuthHeader() });
      setStoreUsers(response.data);
    } catch (error) {
      toast.error('Error al cargar usuarios');
    } finally {
      setLoadingUsers(false);
    }
  }, [getAuthHeader]);

  const loadCustomization = useCallback(async () => {
    setLoadingCustom(true);
    try {
      const res = await axios.get(`${API}/admin/store-customization`, { headers: getAuthHeader() });
      setCustomization(res.data);
      setLogoPreview(res.data.logo_url ? (res.data.logo_url.startsWith('http') ? res.data.logo_url : `${BACKEND_URL}${res.data.logo_url}`) : '');
    } catch (error) {
      toast.error('Error al cargar personalizacion');
    } finally {
      setLoadingCustom(false);
    }
  }, [getAuthHeader]);

  const loadStorefront = useCallback(async () => {
    setLoadingStorefront(true);
    try {
      const [customRes, productsRes] = await Promise.all([
        axios.get(`${API}/admin/store-customization`, { headers: getAuthHeader() }),
        axios.get(`${API}/products?store_id=${user?.store_id}`)
      ]);
      setStorefrontData({
        welcome_text: customRes.data.welcome_text || '',
        banner_url: customRes.data.banner_url || '',
        featured_products: customRes.data.featured_products || [],
        product_order: customRes.data.product_order || [],
        product_card_size: customRes.data.product_card_size || 'medium',
        store_subtitle: customRes.data.store_subtitle || '',
        announcement: customRes.data.announcement || '',
        whatsapp: customRes.data.whatsapp || '',
        instagram: customRes.data.instagram || '',
        facebook: customRes.data.facebook || '',
        show_prices: customRes.data.show_prices !== false,
        layout_style: customRes.data.layout_style || 'grid'
      });
      setBannerPreview(customRes.data.banner_url || '');
      setStoreProducts(productsRes.data);
    } catch (error) {
      toast.error('Error al cargar tienda virtual');
    } finally {
      setLoadingStorefront(false);
    }
  }, [getAuthHeader, user]);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate(adminPath ? `/${adminPath}/login` : (storeDomain ? `/admin/${storeDomain}/login` : '/admin/login'));
      return;
    }
    if ((adminPath && user.admin_path && adminPath !== user.admin_path) || (storeDomain && user.store_domain && storeDomain !== user.store_domain)) {
      logout(); navigate(adminPath ? `/${adminPath}/login` : `/admin/${storeDomain}/login`, { replace: true }); return;
    }
    loadSettings();
    setCredentials(prev => ({ ...prev, newUsername: user.username }));
  }, [user, navigate, authLoading, storeDomain, adminPath, loadSettings]);

  useEffect(() => {
    if (activeTab === 'users' && user) {
      loadStoreUsers();
    }
    if (activeTab === 'customize' && user) {
      loadCustomization();
    }
    if (activeTab === 'storefront' && user) {
      loadStorefront();
    }
  }, [activeTab, user, loadStoreUsers, loadCustomization, loadStorefront]);

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setLogoFile(file);
      setLogoPreview(URL.createObjectURL(file));
    }
  };

  const handleSaveCustomization = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      // Upload logo first if selected
      if (logoFile) {
        const formData = new FormData();
        formData.append('file', logoFile);
        const logoRes = await axios.post(`${API}/admin/store-logo`, formData, {
          headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' }
        });
        customization.logo_url = logoRes.data.logo_url;
      }
      // Save customization data
      const saveData = {
        store_name: customization.store_name,
        primary_color: customization.primary_color,
        secondary_color: customization.secondary_color,
        text_color: customization.text_color,
        phone: customization.phone,
        address: customization.address,
        schedule: customization.schedule,
        owner_name: customization.owner_name,
        logo_url: customization.logo_url,
        product_card_size: customization.product_card_size
      };
      await axios.put(`${API}/admin/store-customization`, saveData, { headers: getAuthHeader() });
      toast.success('Personalización guardada exitosamente');
      setLogoFile(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar personalización');
    } finally {
      setSaving(false);
    }
  };

  const handleSubmitSettings = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.put(`${API}/settings`, settings, { headers: getAuthHeader() });
      toast.success('Configuración guardada exitosamente');
    } catch (error) {
      toast.error('Error al guardar configuración');
    } finally {
      setSaving(false);
    }
  };

  const handleChangeUsername = async (e) => {
    e.preventDefault();
    if (!credentials.newUsername || !credentials.currentPassword) {
      toast.error('Completa todos los campos');
      return;
    }
    if (credUsernameAvailable === false) {
      toast.error('Ese nombre de usuario ya está en uso. Elija otro.');
      return;
    }
    setSaving(true);
    try {
      await axios.put(`${API}/admin/change-username`, {
        new_username: credentials.newUsername,
        password: credentials.currentPassword
      }, { headers: getAuthHeader() });
      toast.success('Usuario actualizado. Por favor, inicia sesión nuevamente.');
      logout();
      navigate(adminPath ? `/${adminPath}/login` : (storeDomain ? `/admin/${storeDomain}/login` : '/admin/login'));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al cambiar usuario');
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (!credentials.currentPassword || !credentials.newPassword) {
      toast.error('Completa todos los campos');
      return;
    }
    if (credentials.newPassword !== credentials.confirmPassword) {
      toast.error('Las contraseñas no coinciden');
      return;
    }
    if (credentials.newPassword.length < 4) {
      toast.error('La contraseña debe tener al menos 4 caracteres');
      return;
    }
    setSaving(true);
    try {
      await axios.put(`${API}/admin/change-password`, {
        current_password: credentials.currentPassword,
        new_password: credentials.newPassword
      }, { headers: getAuthHeader() });
      toast.success('Contraseña actualizada exitosamente');
      setCredentials(prev => ({ ...prev, currentPassword: '', newPassword: '', confirmPassword: '' }));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al cambiar contraseña');
    } finally {
      setSaving(false);
    }
  };

  const openCreateUserForm = () => {
    setEditingUser(null);
    setUserForm({ username: '', password: '', role: 'empleado' });
    setShowUserForm(true);
    setIsCreatingUser(true);
    setShowUserPassword(false);
    setUsernameAvailable(null);
  };

  const openEditUserForm = (u) => {
    setEditingUser(u);
    setUserForm({ username: u.username, password: '', role: u.role });
    setShowUserForm(true);
    setIsCreatingUser(false);
    setShowUserPassword(false);
    setUsernameAvailable(null);
  };

  const checkUsernameAvailability = async (uname) => {
    if (!uname || uname.trim().length < 3) { setUsernameAvailable(null); return; }
    // If editing and username hasn't changed, skip
    if (!isCreatingUser && editingUser && uname === editingUser.username) { setUsernameAvailable(true); return; }
    setCheckingUsername(true);
    try {
      const res = await axios.get(`${API}/public/check-username/${uname.trim()}`);
      setUsernameAvailable(res.data.available);
    } catch {
      setUsernameAvailable(null);
    } finally {
      setCheckingUsername(false);
    }
  };

  const checkCredUsernameAvailability = async (uname) => {
    if (!uname || uname.trim().length < 3) { setCredUsernameAvailable(null); return; }
    if (uname === user?.username) { setCredUsernameAvailable(true); return; }
    setCheckingCredUsername(true);
    try {
      const res = await axios.get(`${API}/public/check-username/${uname.trim()}`);
      setCredUsernameAvailable(res.data.available);
    } catch {
      setCredUsernameAvailable(null);
    } finally {
      setCheckingCredUsername(false);
    }
  };

  const handleSaveUser = async (e) => {
    e.preventDefault();
    if (!userForm.username) {
      toast.error('Ingrese un nombre de usuario');
      return;
    }
    if (usernameAvailable === false) {
      toast.error('Ese nombre de usuario ya está en uso. Elija otro.');
      return;
    }
    if (isCreatingUser && !userForm.password) {
      toast.error('Ingrese una contraseña');
      return;
    }
    setSaving(true);
    try {
      if (isCreatingUser) {
        await axios.post(`${API}/admin/store-users`, userForm, { headers: getAuthHeader() });
        toast.success('Usuario creado exitosamente');
      } else {
        const updateData = { username: userForm.username, role: userForm.role };
        if (userForm.password) updateData.password = userForm.password;
        await axios.put(`${API}/admin/store-users/${editingUser.id}`, updateData, { headers: getAuthHeader() });
        toast.success('Usuario actualizado');
      }
      setShowUserForm(false);
      loadStoreUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar usuario');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteUser = async () => {
    if (!deleteUserConfirm) return;
    try {
      await axios.delete(`${API}/admin/store-users/${deleteUserConfirm.id}`, { headers: getAuthHeader() });
      toast.success('Usuario eliminado');
      setDeleteUserConfirm(null);
      loadStoreUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar usuario');
      setDeleteUserConfirm(null);
    }
  };

  const handleLogout = () => {
    logout();
    navigate(adminPath ? `/${adminPath}/login` : (storeDomain ? `/admin/${storeDomain}/login` : '/admin/login'));
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Settings size={48} className="animate-spin text-accent-text" />
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: 'var(--bg-section)', overflowX: 'hidden' }}>
      {/* Header */}
      <div className="bg-white border-b" style={{ borderColor: 'var(--border-light)' }}>
        <div className="container py-3">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 min-w-0">
              <Settings className="text-accent-text flex-shrink-0" size={24} />
              <div className="min-w-0">
                <h1 className="text-base md:text-lg font-bold truncate">Configuración</h1>
                <p className="text-xs text-text-muted">Tienda y usuarios</p>
              </div>
            </div>
            <div className="flex gap-2 flex-shrink-0">
              <button onClick={() => navigate(`${basePath}/dashboard`)} className="btn-secondary text-sm">
                <Package size={16} className="mr-1" />
                <span className="hidden sm:inline">Productos</span>
              </button>
              <button onClick={handleLogout} className="btn-secondary text-sm">
                <LogOut size={16} />
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="container py-4 md:py-8">
        <div className="max-w-2xl mx-auto">
          {/* Tabs */}
          <div className="flex flex-wrap gap-2 mb-6">
            <button
              onClick={() => setActiveTab('store')}
              data-testid="tab-store"
              className={`flex-shrink-0 py-3 px-3 md:px-4 rounded-lg font-medium transition-all text-sm ${
                activeTab === 'store' 
                  ? 'bg-accent-wash text-accent-text border border-accent-text' 
                  : 'bg-white border'
              }`}
              style={{ borderColor: activeTab === 'store' ? 'var(--accent-primary)' : 'var(--border-light)' }}
            >
              <Store size={18} className="inline mr-1 md:mr-2" />
              Tienda
            </button>
            <button
              onClick={() => setActiveTab('credentials')}
              data-testid="tab-credentials"
              className={`flex-shrink-0 py-3 px-3 md:px-4 rounded-lg font-medium transition-all text-sm ${
                activeTab === 'credentials' 
                  ? 'bg-accent-wash text-accent-text border border-accent-text' 
                  : 'bg-white border'
              }`}
              style={{ borderColor: activeTab === 'credentials' ? 'var(--accent-primary)' : 'var(--border-light)' }}
            >
              <Lock size={18} className="inline mr-1 md:mr-2" />
              Mi Cuenta
            </button>
            {hasPermission(['admin']) && (
              <button
                onClick={() => setActiveTab('users')}
                data-testid="tab-users"
                className={`flex-shrink-0 py-3 px-3 md:px-4 rounded-lg font-medium transition-all text-sm ${
                  activeTab === 'users' 
                    ? 'bg-accent-wash text-accent-text border border-accent-text' 
                    : 'bg-white border'
                }`}
                style={{ borderColor: activeTab === 'users' ? 'var(--accent-primary)' : 'var(--border-light)' }}
              >
                <Users size={18} className="inline mr-1 md:mr-2" />
                Usuarios
              </button>
            )}
            {hasPermission(['admin']) && (
              <button
                onClick={() => setActiveTab('customize')}
                data-testid="tab-customize"
                className={`flex-shrink-0 py-3 px-3 md:px-4 rounded-lg font-medium transition-all text-sm ${
                  activeTab === 'customize' 
                    ? 'bg-accent-wash text-accent-text border border-accent-text' 
                    : 'bg-white border'
                }`}
                style={{ borderColor: activeTab === 'customize' ? 'var(--accent-primary)' : 'var(--border-light)' }}
              >
                <Palette size={18} className="inline mr-1 md:mr-2" />
                Personalizar
              </button>
            )}
            {hasPermission(['admin']) && (
              <button
                onClick={() => setActiveTab('storefront')}
                data-testid="tab-storefront"
                className={`flex-shrink-0 py-3 px-3 md:px-4 rounded-lg font-medium transition-all text-sm ${
                  activeTab === 'storefront' 
                    ? 'bg-accent-wash text-accent-text border border-accent-text' 
                    : 'bg-white border'
                }`}
                style={{ borderColor: activeTab === 'storefront' ? 'var(--accent-primary)' : 'var(--border-light)' }}
              >
                <ShoppingBag size={18} className="inline mr-1 md:mr-2" />
                Tienda Virtual
              </button>
            )}
            {hasPermission(['admin']) && (
              <button
                onClick={() => setActiveTab('billing')}
                data-testid="tab-billing"
                className={`flex-shrink-0 py-3 px-3 md:px-4 rounded-lg font-medium transition-all text-sm ${
                  activeTab === 'billing' 
                    ? 'bg-accent-wash text-accent-text border border-accent-text' 
                    : 'bg-white border'
                }`}
                style={{ borderColor: activeTab === 'billing' ? 'var(--accent-primary)' : 'var(--border-light)' }}
              >
                <FileText size={18} className="inline mr-1 md:mr-2" />
                Facturación
              </button>
            )}
          </div>

          {/* Store Settings Tab */}
          {activeTab === 'store' && (
            <form onSubmit={handleSubmitSettings} className="bg-white rounded-xl border p-4 md:p-6 space-y-5" style={{ borderColor: 'var(--border-light)' }}>
              <div>
                <label className="flex items-center gap-2 text-sm font-medium text-text-primary mb-2">
                  <Store size={18} className="text-accent-text" />
                  Nombre de la Tienda
                </label>
                <input type="text" value={settings.store_name}
                  onChange={(e) => setSettings({ ...settings, store_name: e.target.value })}
                  className="w-full px-4 py-3 rounded-lg border focus:outline-none focus:ring-2"
                  style={{ borderColor: 'var(--border-light)' }} placeholder="SuperMercado Digital" />
              </div>
              <div>
                <label className="flex items-center gap-2 text-sm font-medium text-text-primary mb-2">
                  <Mail size={18} className="text-accent-text" />
                  Correo Electrónico
                </label>
                <input type="email" value={settings.email}
                  onChange={(e) => setSettings({ ...settings, email: e.target.value })}
                  className="w-full px-4 py-3 rounded-lg border focus:outline-none focus:ring-2"
                  style={{ borderColor: 'var(--border-light)' }} placeholder="info@supermercado.ec" />
              </div>
              <div>
                <label className="flex items-center gap-2 text-sm font-medium text-text-primary mb-2">
                  <Phone size={18} className="text-accent-text" />
                  Teléfono de Contacto
                </label>
                <input type="tel" value={settings.phone}
                  onChange={(e) => setSettings({ ...settings, phone: e.target.value })}
                  className="w-full px-4 py-3 rounded-lg border focus:outline-none focus:ring-2"
                  style={{ borderColor: 'var(--border-light)' }} placeholder="0999999999" />
              </div>
              <div>
                <label className="flex items-center gap-2 text-sm font-medium text-text-primary mb-2">
                  <MapPin size={18} className="text-accent-text" />
                  Ubicación / Dirección
                </label>
                <input type="text" value={settings.address}
                  onChange={(e) => setSettings({ ...settings, address: e.target.value })}
                  className="w-full px-4 py-3 rounded-lg border focus:outline-none focus:ring-2"
                  style={{ borderColor: 'var(--border-light)' }} placeholder="Ecuador" />
              </div>
              <div>
                <label className="flex items-center gap-2 text-sm font-medium text-text-primary mb-2">
                  <Clock size={18} className="text-accent-text" />
                  Horario de Atención
                </label>
                <input type="text" value={settings.schedule}
                  onChange={(e) => setSettings({ ...settings, schedule: e.target.value })}
                  className="w-full px-4 py-3 rounded-lg border focus:outline-none focus:ring-2"
                  style={{ borderColor: 'var(--border-light)' }} placeholder="Lunes a Domingo: 9:00 AM - 10:00 PM" />
              </div>
              <div>
                <label className="flex items-center gap-2 font-medium mb-2">
                  <Clock size={18} className="text-accent-text" />
                  Zona Horaria
                </label>
                <select value={settings.timezone || 'America/Guayaquil'}
                  onChange={(e) => setSettings({ ...settings, timezone: e.target.value })}
                  className="w-full px-4 py-3 rounded-lg border focus:outline-none focus:ring-2"
                  style={{ borderColor: 'var(--border-light)' }}
                  data-testid="timezone-select">
                  <optgroup label="Ecuador">
                    <option value="America/Guayaquil">Ecuador Continental (UTC-5)</option>
                    <option value="Pacific/Galapagos">Galápagos (UTC-6)</option>
                  </optgroup>
                  <optgroup label="América Latina">
                    <option value="America/Bogota">Colombia (UTC-5)</option>
                    <option value="America/Lima">Perú (UTC-5)</option>
                    <option value="America/Mexico_City">México Central (UTC-6)</option>
                    <option value="America/Cancun">México Quintana Roo (UTC-5)</option>
                    <option value="America/Santiago">Chile (UTC-3/-4)</option>
                    <option value="America/Argentina/Buenos_Aires">Argentina (UTC-3)</option>
                    <option value="America/Sao_Paulo">Brasil São Paulo (UTC-3)</option>
                    <option value="America/Caracas">Venezuela (UTC-4)</option>
                    <option value="America/La_Paz">Bolivia (UTC-4)</option>
                    <option value="America/Panama">Panamá (UTC-5)</option>
                    <option value="America/Costa_Rica">Costa Rica (UTC-6)</option>
                    <option value="America/Guatemala">Guatemala (UTC-6)</option>
                  </optgroup>
                  <optgroup label="Estados Unidos">
                    <option value="America/New_York">Eastern (UTC-5/-4)</option>
                    <option value="America/Chicago">Central (UTC-6/-5)</option>
                    <option value="America/Denver">Mountain (UTC-7/-6)</option>
                    <option value="America/Los_Angeles">Pacific (UTC-8/-7)</option>
                  </optgroup>
                  <optgroup label="Europa">
                    <option value="Europe/Madrid">España (UTC+1/+2)</option>
                    <option value="Europe/London">Reino Unido (UTC+0/+1)</option>
                    <option value="Europe/Paris">Francia (UTC+1/+2)</option>
                  </optgroup>
                </select>
              </div>
              <button type="submit" className="btn-primary w-full" disabled={saving}>
                <Save size={20} className="mr-2" />
                {saving ? 'Guardando...' : 'Guardar Configuración'}
              </button>
            </form>
          )}

          {/* Credentials Tab */}
          {activeTab === 'credentials' && (
            <div className="space-y-6">
              {/* Change Username */}
              <form onSubmit={handleChangeUsername} className="bg-white rounded-xl border p-4 md:p-6 space-y-4" style={{ borderColor: 'var(--border-light)' }}>
                <h3 className="font-bold flex items-center gap-2">
                  <User size={20} className="text-accent-text" />
                  Cambiar Mi Usuario
                </h3>
                <div>
                  <label className="block text-sm font-medium mb-1">Usuario Actual</label>
                  <input type="text" value={user?.username || ''} disabled
                    className="w-full px-4 py-3 rounded-lg border bg-gray-50"
                    style={{ borderColor: 'var(--border-light)' }} />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Nuevo Usuario</label>
                  <div className="relative">
                    <input type="text" value={credentials.newUsername}
                      onChange={(e) => { setCredentials({ ...credentials, newUsername: e.target.value }); setCredUsernameAvailable(null); }}
                      onBlur={() => checkCredUsernameAvailability(credentials.newUsername)}
                      className="w-full px-4 py-3 rounded-lg border focus:outline-none focus:ring-2 pr-12"
                      style={{ borderColor: credUsernameAvailable === false ? '#ef4444' : credUsernameAvailable === true ? '#22c55e' : 'var(--border-light)', transition: 'border-color 0.2s' }}
                      placeholder="Nuevo nombre de usuario" />
                    {credUsernameAvailable === true && <CheckCircle size={18} className="absolute right-3 top-1/2 -translate-y-1/2 text-green-500" />}
                    {credUsernameAvailable === false && <AlertCircle size={18} className="absolute right-3 top-1/2 -translate-y-1/2 text-red-500" />}
                  </div>
                  {checkingCredUsername && <p className="text-xs text-blue-500 mt-1">Verificando disponibilidad...</p>}
                  {credUsernameAvailable === false && <p className="text-xs text-red-500 mt-1">Ese usuario no está disponible</p>}
                  {credUsernameAvailable === true && credentials.newUsername !== user?.username && <p className="text-xs text-green-500 mt-1">Usuario disponible</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Contraseña (para confirmar)</label>
                  <div className="relative">
                    <input type={showPasswords.current ? 'text' : 'password'} value={credentials.currentPassword}
                      onChange={(e) => setCredentials({ ...credentials, currentPassword: e.target.value })}
                      className="w-full px-4 py-3 rounded-lg border focus:outline-none focus:ring-2 pr-12"
                      style={{ borderColor: 'var(--border-light)' }} placeholder="Tu contraseña actual" />
                    <button type="button"
                      onClick={() => setShowPasswords({ ...showPasswords, current: !showPasswords.current })}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-text-muted">
                      {showPasswords.current ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>
                <button type="submit" className="btn-primary w-full" disabled={saving}>
                  {saving ? 'Guardando...' : 'Cambiar Usuario'}
                </button>
              </form>

              {/* Change Password */}
              <form onSubmit={handleChangePassword} className="bg-white rounded-xl border p-4 md:p-6 space-y-4" style={{ borderColor: 'var(--border-light)' }}>
                <h3 className="font-bold flex items-center gap-2">
                  <Lock size={20} className="text-accent-text" />
                  Cambiar Mi Contraseña
                </h3>
                <div>
                  <label className="block text-sm font-medium mb-1">Contraseña Actual</label>
                  <div className="relative">
                    <input type={showPasswords.current ? 'text' : 'password'} value={credentials.currentPassword}
                      onChange={(e) => setCredentials({ ...credentials, currentPassword: e.target.value })}
                      className="w-full px-4 py-3 rounded-lg border focus:outline-none focus:ring-2 pr-12"
                      style={{ borderColor: 'var(--border-light)' }} placeholder="Contraseña actual" />
                    <button type="button"
                      onClick={() => setShowPasswords({ ...showPasswords, current: !showPasswords.current })}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-text-muted">
                      {showPasswords.current ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Nueva Contraseña</label>
                  <div className="relative">
                    <input type={showPasswords.new ? 'text' : 'password'} value={credentials.newPassword}
                      onChange={(e) => setCredentials({ ...credentials, newPassword: e.target.value })}
                      className="w-full px-4 py-3 rounded-lg border focus:outline-none focus:ring-2 pr-12"
                      style={{ borderColor: 'var(--border-light)' }} placeholder="Nueva contraseña" />
                    <button type="button"
                      onClick={() => setShowPasswords({ ...showPasswords, new: !showPasswords.new })}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-text-muted">
                      {showPasswords.new ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Confirmar Nueva Contraseña</label>
                  <div className="relative">
                    <input type={showPasswords.confirm ? 'text' : 'password'} value={credentials.confirmPassword}
                      onChange={(e) => setCredentials({ ...credentials, confirmPassword: e.target.value })}
                      className="w-full px-4 py-3 rounded-lg border focus:outline-none focus:ring-2 pr-12"
                      style={{ borderColor: 'var(--border-light)' }} placeholder="Confirmar contraseña" />
                    <button type="button"
                      onClick={() => setShowPasswords({ ...showPasswords, confirm: !showPasswords.confirm })}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-text-muted">
                      {showPasswords.confirm ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>
                <button type="submit" className="btn-primary w-full" disabled={saving}>
                  {saving ? 'Guardando...' : 'Cambiar Contraseña'}
                </button>
              </form>
            </div>
          )}

          {/* Users Management Tab */}
          {activeTab === 'users' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="font-bold text-lg flex items-center gap-2">
                  <Users size={22} className="text-accent-text" />
                  Usuarios de la Tienda
                </h3>
                <button onClick={openCreateUserForm} className="btn-primary" data-testid="create-user-btn">
                  <Plus size={18} className="mr-2" />
                  Nuevo Usuario
                </button>
              </div>

              {loadingUsers ? (
                <div className="text-center py-8">
                  <Users size={32} className="mx-auto animate-spin text-accent-text mb-2" />
                  <p className="text-text-muted">Cargando usuarios...</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {storeUsers.map((u) => (
                    <div key={u.id} className="bg-white rounded-xl border p-4 flex items-center justify-between"
                      style={{ borderColor: 'var(--border-light)' }} data-testid={`user-card-${u.id}`}>
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-full flex items-center justify-center"
                          style={{ background: 'var(--accent-wash)' }}>
                          <User size={20} className="text-accent-text" />
                        </div>
                        <div>
                          <p className="font-medium text-text-primary">{u.username}</p>
                          <div className="flex items-center gap-2 mt-1">
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${ROLE_COLORS[u.role] || 'bg-gray-100 text-gray-700'}`}>
                              <Shield size={12} className="inline mr-1" />
                              {ROLE_LABELS[u.role] || u.role}
                            </span>
                            {u.plain_password && (
                              <span className="text-xs text-text-muted">
                                Clave: <span className="font-mono bg-gray-100 px-1 rounded">{u.plain_password}</span>
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <button onClick={() => openEditUserForm(u)}
                          className="p-2 hover:bg-accent-wash rounded-lg transition-colors"
                          title="Editar" data-testid={`edit-user-${u.id}`}>
                          <Edit size={18} className="text-accent-text" />
                        </button>
                        {u.id !== user?.token && u.role !== 'admin' && (
                          <button onClick={() => setDeleteUserConfirm(u)}
                            className="p-2 hover:bg-red-50 rounded-lg transition-colors"
                            title="Eliminar" data-testid={`delete-user-${u.id}`}>
                            <Trash2 size={18} className="text-red-500" />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                  {storeUsers.length === 0 && (
                    <div className="text-center py-8 bg-white rounded-xl border" style={{ borderColor: 'var(--border-light)' }}>
                      <Users size={40} className="mx-auto text-text-muted mb-2" />
                      <p className="text-text-muted">No hay usuarios registrados</p>
                    </div>
                  )}
                </div>
              )}

              {/* User Form Modal */}
              {showUserForm && (
                <>
                  <div className="fixed inset-0 bg-black bg-opacity-50 z-40" onClick={() => setShowUserForm(false)} />
                  <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-xl shadow-2xl max-w-md w-full" data-testid="user-form-modal">
                      <div className="p-6 border-b" style={{ borderColor: 'var(--border-light)' }}>
                        <div className="flex items-center justify-between">
                          <h3 className="font-bold text-lg">
                            {isCreatingUser ? 'Nuevo Usuario' : `Editar: ${editingUser?.username}`}
                          </h3>
                          <button onClick={() => setShowUserForm(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                            <X size={20} />
                          </button>
                        </div>
                      </div>
                      <form onSubmit={handleSaveUser} className="p-6 space-y-4">
                        <div>
                          <label className="block text-sm font-medium mb-1">Nombre de Usuario</label>
                          <div className="relative">
                            <input type="text" value={userForm.username}
                              onChange={(e) => { setUserForm({ ...userForm, username: e.target.value }); setUsernameAvailable(null); }}
                              onBlur={() => checkUsernameAvailability(userForm.username)}
                              required className="w-full px-4 py-3 rounded-lg border focus:outline-none focus:ring-2 pr-12"
                              style={{ borderColor: usernameAvailable === false ? '#ef4444' : usernameAvailable === true ? '#22c55e' : 'var(--border-light)', transition: 'border-color 0.2s' }}
                              placeholder="nombre_usuario"
                              data-testid="user-form-username" />
                            {usernameAvailable === true && <CheckCircle size={18} className="absolute right-3 top-1/2 -translate-y-1/2 text-green-500" />}
                            {usernameAvailable === false && <AlertCircle size={18} className="absolute right-3 top-1/2 -translate-y-1/2 text-red-500" />}
                          </div>
                          {checkingUsername && <p className="text-xs text-blue-500 mt-1">Verificando disponibilidad...</p>}
                          {usernameAvailable === false && <p className="text-xs text-red-500 mt-1" data-testid="username-not-available">Ese usuario no está disponible, elige otro</p>}
                          {usernameAvailable === true && <p className="text-xs text-green-500 mt-1" data-testid="username-available">Usuario disponible</p>}
                        </div>
                        <div>
                          <label className="block text-sm font-medium mb-1">
                            {isCreatingUser ? 'Contraseña' : 'Nueva Contraseña (dejar vacío para no cambiar)'}
                          </label>
                          <div className="relative">
                            <input type={showUserPassword ? 'text' : 'password'} value={userForm.password}
                              onChange={(e) => setUserForm({ ...userForm, password: e.target.value })}
                              required={isCreatingUser}
                              className="w-full px-4 py-3 rounded-lg border focus:outline-none focus:ring-2 pr-12"
                              style={{ borderColor: 'var(--border-light)' }} placeholder={isCreatingUser ? 'Contraseña' : 'Dejar vacío para mantener'}
                              data-testid="user-form-password" />
                            <button type="button" onClick={() => setShowUserPassword(!showUserPassword)}
                              className="absolute right-3 top-1/2 -translate-y-1/2 text-text-muted">
                              {showUserPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                            </button>
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium mb-1">Rol</label>
                          <select value={userForm.role}
                            onChange={(e) => setUserForm({ ...userForm, role: e.target.value })}
                            className="w-full px-4 py-3 rounded-lg border focus:outline-none focus:ring-2"
                            style={{ borderColor: 'var(--border-light)' }}
                            data-testid="user-form-role">
                            <option value="admin">Administrador</option>
                            <option value="empleado">Empleado (Cajero)</option>
                            <option value="mesera">Mesera</option>
                            <option value="inventario">Inventario (Bodega)</option>
                            <option value="cocina">Cocina</option>
                          </select>
                        </div>
                        <div className="flex gap-3 pt-2">
                          <button type="submit" className="btn-primary flex-1" disabled={saving}
                            data-testid="user-form-save">
                            <Save size={18} className="mr-2" />
                            {saving ? 'Guardando...' : isCreatingUser ? 'Crear Usuario' : 'Guardar Cambios'}
                          </button>
                          <button type="button" onClick={() => setShowUserForm(false)} className="btn-secondary">
                            Cancelar
                          </button>
                        </div>
                      </form>
                    </div>
                  </div>
                </>
              )}

              {/* Delete User Confirm Modal */}
              {deleteUserConfirm && (
                <>
                  <div className="fixed inset-0 bg-black bg-opacity-50 z-50" onClick={() => setDeleteUserConfirm(null)} />
                  <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-xl shadow-2xl p-6 max-w-sm w-full" data-testid="delete-user-modal">
                      <div className="text-center mb-4">
                        <Trash2 size={40} className="mx-auto text-red-500 mb-3" />
                        <h3 className="font-bold text-lg">Eliminar Usuario</h3>
                        <p className="text-text-muted mt-2">
                          Eliminar a <strong>"{deleteUserConfirm.username}"</strong> ({ROLE_LABELS[deleteUserConfirm.role]})?
                        </p>
                      </div>
                      <div className="flex gap-3">
                        <button onClick={() => setDeleteUserConfirm(null)} className="btn-secondary flex-1">Cancelar</button>
                        <button onClick={handleDeleteUser}
                          className="flex-1 px-4 py-2 rounded-lg text-white font-medium transition-colors"
                          style={{ background: '#ef4444' }} data-testid="confirm-delete-user">
                          Eliminar
                        </button>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}

          {/* Customization Tab */}
          {activeTab === 'customize' && (
            <div className="space-y-6">
              {loadingCustom ? (
                <div className="text-center py-8">
                  <Palette size={32} className="mx-auto animate-spin text-accent-text mb-2" />
                  <p className="text-text-muted">Cargando personalización...</p>
                </div>
              ) : (
                <form onSubmit={handleSaveCustomization} className="space-y-6">
                  {/* Public Link */}
                  <div className="bg-white rounded-xl border p-4 md:p-6" style={{ borderColor: 'var(--border-light)' }}>
                    <h3 className="font-bold mb-4 flex items-center gap-2">
                      <Globe size={20} className="text-accent-text" />
                      Enlace de tu Tienda Online
                    </h3>
                    <div className="flex items-center gap-2 p-3 rounded-lg" style={{ background: 'var(--accent-wash)', border: '1px solid var(--accent-primary)' }}>
                      <Globe size={16} className="text-accent-text flex-shrink-0" />
                      <span className="text-sm font-mono text-accent-text flex-1 truncate" data-testid="store-public-link">
                        {window.location.origin}/tienda/{customization.domain}
                      </span>
                      <button type="button"
                        onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/tienda/${customization.domain}`); toast.success('Enlace copiado!'); }}
                        className="p-1.5 hover:bg-white rounded flex-shrink-0" data-testid="copy-store-link">
                        <Copy size={16} className="text-accent-text" />
                      </button>
                    </div>
                  </div>

                  {/* Logo */}
                  <div className="bg-white rounded-xl border p-4 md:p-6" style={{ borderColor: 'var(--border-light)' }}>
                    <h3 className="font-bold mb-4 flex items-center gap-2">
                      <Image size={20} className="text-accent-text" />
                      Logo de la Tienda
                    </h3>
                    <div className="flex items-center gap-6">
                      <div className="w-24 h-24 rounded-xl border-2 border-dashed flex items-center justify-center overflow-hidden"
                        style={{ borderColor: 'var(--border-light)' }}>
                        {logoPreview ? (
                          <img src={logoPreview} alt="Logo" className="w-full h-full object-cover" data-testid="logo-preview" />
                        ) : (
                          <Store size={32} className="text-text-muted" />
                        )}
                      </div>
                      <div className="flex-1">
                        <label className="btn-secondary cursor-pointer inline-flex items-center gap-2">
                          <Upload size={16} />
                          <span>Subir Logo</span>
                          <input type="file" accept="image/*" onChange={handleLogoChange} className="hidden" data-testid="logo-upload" />
                        </label>
                        <p className="text-xs text-text-muted mt-2">JPG, PNG o WEBP. Recomendado: 200x200px</p>
                      </div>
                    </div>
                  </div>

                  {/* Colors */}
                  <div className="bg-white rounded-xl border p-4 md:p-6" style={{ borderColor: 'var(--border-light)' }}>
                    <h3 className="font-bold mb-4 flex items-center gap-2">
                      <Palette size={20} className="text-accent-text" />
                      Colores de la Tienda
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium mb-2">Color Principal</label>
                        <div className="flex items-center gap-3">
                          <input type="color" value={customization.primary_color}
                            onChange={(e) => setCustomization({ ...customization, primary_color: e.target.value })}
                            className="w-12 h-12 rounded-lg border cursor-pointer" style={{ borderColor: 'var(--border-light)' }}
                            data-testid="primary-color-input" />
                          <input type="text" value={customization.primary_color}
                            onChange={(e) => setCustomization({ ...customization, primary_color: e.target.value })}
                            className="flex-1 px-3 py-2 rounded-lg border text-sm font-mono"
                            style={{ borderColor: 'var(--border-light)' }} />
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2">Color de Fondo</label>
                        <div className="flex items-center gap-3">
                          <input type="color" value={customization.secondary_color}
                            onChange={(e) => setCustomization({ ...customization, secondary_color: e.target.value })}
                            className="w-12 h-12 rounded-lg border cursor-pointer" style={{ borderColor: 'var(--border-light)' }}
                            data-testid="secondary-color-input" />
                          <input type="text" value={customization.secondary_color}
                            onChange={(e) => setCustomization({ ...customization, secondary_color: e.target.value })}
                            className="flex-1 px-3 py-2 rounded-lg border text-sm font-mono"
                            style={{ borderColor: 'var(--border-light)' }} />
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2">Color de Texto</label>
                        <div className="flex items-center gap-3">
                          <input type="color" value={customization.text_color}
                            onChange={(e) => setCustomization({ ...customization, text_color: e.target.value })}
                            className="w-12 h-12 rounded-lg border cursor-pointer" style={{ borderColor: 'var(--border-light)' }}
                            data-testid="text-color-input" />
                          <input type="text" value={customization.text_color}
                            onChange={(e) => setCustomization({ ...customization, text_color: e.target.value })}
                            className="flex-1 px-3 py-2 rounded-lg border text-sm font-mono"
                            style={{ borderColor: 'var(--border-light)' }} />
                        </div>
                      </div>
                    </div>
                    {/* Color Preview */}
                    <div className="mt-4 p-4 rounded-xl" style={{ background: customization.secondary_color }}>
                      <p style={{ color: customization.text_color, fontWeight: 600 }}>Vista previa de colores</p>
                      <div className="flex gap-3 mt-2">
                        <span className="px-4 py-2 rounded-lg text-white text-sm font-medium" style={{ background: customization.primary_color }}>Botón</span>
                        <span className="px-4 py-2 rounded-lg text-sm font-medium border" style={{ color: customization.primary_color, borderColor: customization.primary_color }}>Secundario</span>
                      </div>
                    </div>
                  </div>

                  {/* Product Card Size */}
                  <div className="bg-white rounded-xl border p-4 md:p-6" style={{ borderColor: 'var(--border-light)' }}>
                    <h3 className="font-bold mb-3 md:mb-4 flex items-center gap-2 text-sm md:text-base">
                      <Package size={18} className="text-accent-text" />
                      Tamaño de Productos en Tienda
                    </h3>
                    <p className="text-xs md:text-sm text-text-muted mb-3 md:mb-4">Elige cómo se ven los productos en tu tienda pública</p>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 md:gap-3">
                      {[
                        { value: 'small', label: 'Pequeño', desc: 'Compacto, más productos por fila', gridIcon: '▪▪▪▪' },
                        { value: 'medium', label: 'Mediano', desc: 'Balance entre tamaño y cantidad', gridIcon: '▪▪▪' },
                        { value: 'large', label: 'Grande', desc: 'Fotos grandes, menos por fila', gridIcon: '▪▪' }
                      ].map(opt => (
                        <button
                          key={opt.value}
                          type="button"
                          onClick={() => setCustomization({ ...customization, product_card_size: opt.value })}
                          data-testid={`card-size-${opt.value}`}
                          className="p-4 rounded-xl border-2 transition-all text-left"
                          style={{
                            borderColor: customization.product_card_size === opt.value ? customization.primary_color : 'var(--border-light)',
                            background: customization.product_card_size === opt.value ? `${customization.primary_color}10` : 'white'
                          }}
                        >
                          <div className="text-2xl mb-2 tracking-wider text-center" style={{ color: customization.product_card_size === opt.value ? customization.primary_color : '#ccc' }}>
                            {opt.gridIcon}
                          </div>
                          <p className="font-semibold text-sm" style={{ color: customization.product_card_size === opt.value ? customization.primary_color : 'var(--text-primary)' }}>
                            {opt.label}
                          </p>
                          <p className="text-xs text-text-muted mt-1">{opt.desc}</p>
                        </button>
                      ))}
                    </div>
                    {/* Live Preview */}
                    <div className="mt-4 p-4 rounded-xl border" style={{ borderColor: 'var(--border-light)', background: '#fafafa' }}>
                      <p className="text-xs text-text-muted mb-3 text-center">Vista previa del grid</p>
                      <div style={{
                        display: 'grid',
                        gridTemplateColumns: customization.product_card_size === 'small' ? 'repeat(4, 1fr)' : customization.product_card_size === 'large' ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)',
                        gap: '8px'
                      }}>
                        {Array.from({ length: customization.product_card_size === 'small' ? 8 : customization.product_card_size === 'large' ? 4 : 6 }).map((_, i) => (
                          <div key={i} className="rounded-lg" style={{
                            background: i % 2 === 0 ? customization.primary_color + '30' : customization.secondary_color,
                            height: customization.product_card_size === 'small' ? '40px' : customization.product_card_size === 'large' ? '70px' : '50px',
                            border: `1px solid ${customization.primary_color}20`
                          }} />
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Store Info */}
                  <div className="bg-white rounded-xl border p-4 md:p-6" style={{ borderColor: 'var(--border-light)' }}>
                    <h3 className="font-bold mb-4 flex items-center gap-2">
                      <Store size={20} className="text-accent-text" />
                      Datos de la Tienda
                    </h3>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium mb-1">Nombre de la Tienda</label>
                        <input type="text" value={customization.store_name}
                          onChange={(e) => setCustomization({ ...customization, store_name: e.target.value })}
                          className="w-full px-4 py-3 rounded-lg border focus:outline-none focus:ring-2"
                          style={{ borderColor: 'var(--border-light)' }} placeholder="Mi Tienda"
                          data-testid="custom-store-name" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-1">Propietario</label>
                        <input type="text" value={customization.owner_name}
                          onChange={(e) => setCustomization({ ...customization, owner_name: e.target.value })}
                          className="w-full px-4 py-3 rounded-lg border focus:outline-none focus:ring-2"
                          style={{ borderColor: 'var(--border-light)' }} placeholder="Juan Pérez"
                          data-testid="custom-owner-name" />
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium mb-1">Teléfono</label>
                          <input type="tel" value={customization.phone}
                            onChange={(e) => setCustomization({ ...customization, phone: e.target.value })}
                            className="w-full px-4 py-3 rounded-lg border focus:outline-none focus:ring-2"
                            style={{ borderColor: 'var(--border-light)' }} placeholder="0991234567"
                            data-testid="custom-phone" />
                        </div>
                        <div>
                          <label className="block text-sm font-medium mb-1">Horario</label>
                          <input type="text" value={customization.schedule}
                            onChange={(e) => setCustomization({ ...customization, schedule: e.target.value })}
                            className="w-full px-4 py-3 rounded-lg border focus:outline-none focus:ring-2"
                            style={{ borderColor: 'var(--border-light)' }} placeholder="Lun-Dom: 9AM - 10PM"
                            data-testid="custom-schedule" />
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-1">Dirección</label>
                        <input type="text" value={customization.address}
                          onChange={(e) => setCustomization({ ...customization, address: e.target.value })}
                          className="w-full px-4 py-3 rounded-lg border focus:outline-none focus:ring-2"
                          style={{ borderColor: 'var(--border-light)' }} placeholder="Av. Principal 123, Ciudad"
                          data-testid="custom-address" />
                      </div>
                    </div>
                  </div>

                  <button type="submit" className="btn-primary w-full" disabled={saving} data-testid="save-customization">
                    <Save size={20} className="mr-2" />
                    {saving ? 'Guardando...' : 'Guardar Personalización'}
                  </button>
                </form>
              )}
            </div>
          )}

          {/* Storefront Editor Tab */}
          {activeTab === 'storefront' && (
            <div className="space-y-6">
              {loadingStorefront ? (
                <div className="text-center py-8">
                  <ShoppingBag size={32} className="mx-auto animate-spin text-accent-text mb-2" />
                  <p className="text-text-muted">Cargando editor...</p>
                </div>
              ) : (
                <>
                  {/* Banner */}
                  <div className="bg-white rounded-xl border p-4 md:p-6" style={{ borderColor: 'var(--border-light)' }}>
                    <h3 className="font-bold mb-4 flex items-center gap-2">
                      <Image size={20} className="text-accent-text" />
                      Banner Principal
                    </h3>
                    {(bannerPreview || storefrontData.banner_url) && (
                      <div className="mb-3 relative rounded-lg overflow-hidden" style={{ height: '140px' }}>
                        <img src={bannerPreview || storefrontData.banner_url} alt="Banner" className="w-full h-full object-cover" />
                        <button type="button" onClick={() => { setBannerFile(null); setBannerPreview(''); setStorefrontData(d => ({ ...d, banner_url: '' })); }}
                          className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600">
                          <X size={14} />
                        </button>
                      </div>
                    )}
                    <label className="btn-secondary w-full flex items-center justify-center gap-2 cursor-pointer" data-testid="upload-banner-btn">
                      <Upload size={18} /> {bannerFile ? 'Cambiar Banner' : 'Subir Banner'}
                      <input type="file" accept="image/*" className="hidden" onChange={(e) => {
                        const file = e.target.files[0];
                        if (file) { setBannerFile(file); setBannerPreview(URL.createObjectURL(file)); }
                      }} />
                    </label>
                    <p className="text-xs text-text-muted mt-2">Recomendado: 1200x400px. Se muestra como fondo del hero.</p>
                  </div>

                  {/* Welcome Text */}
                  <div className="bg-white rounded-xl border p-4 md:p-6" style={{ borderColor: 'var(--border-light)' }}>
                    <h3 className="font-bold mb-4 flex items-center gap-2">
                      <Edit size={20} className="text-accent-text" />
                      Texto de Bienvenida
                    </h3>
                    <input type="text" value={storefrontData.welcome_text}
                      onChange={(e) => setStorefrontData(d => ({ ...d, welcome_text: e.target.value }))}
                      className="w-full px-4 py-3 rounded-lg border focus:outline-none focus:ring-2"
                      style={{ borderColor: 'var(--border-light)' }}
                      placeholder="Ej: Bienvenido a Mi Tienda - Los mejores productos!"
                      data-testid="welcome-text-input" />
                    <p className="text-xs text-text-muted mt-1">Si lo dejas vacio, mostrara "Bienvenido a [nombre tienda]"</p>
                  </div>

                  {/* Subtitle */}
                  <div className="bg-white rounded-xl border p-4 md:p-6" style={{ borderColor: 'var(--border-light)' }}>
                    <h3 className="font-bold mb-4 flex items-center gap-2">
                      <Edit size={20} className="text-accent-text" />
                      Subtitulo
                    </h3>
                    <input type="text" value={storefrontData.store_subtitle}
                      onChange={(e) => setStorefrontData(d => ({ ...d, store_subtitle: e.target.value }))}
                      className="w-full px-4 py-3 rounded-lg border focus:outline-none focus:ring-2"
                      style={{ borderColor: 'var(--border-light)' }}
                      placeholder="Ej: Tu tienda de confianza desde 2020"
                      data-testid="subtitle-input" />
                  </div>

                  {/* Announcement Bar */}
                  <div className="bg-white rounded-xl border p-4 md:p-6" style={{ borderColor: 'var(--border-light)' }}>
                    <h3 className="font-bold mb-4 flex items-center gap-2">
                      <Package size={20} className="text-accent-text" />
                      Barra de Anuncio
                    </h3>
                    <input type="text" value={storefrontData.announcement}
                      onChange={(e) => setStorefrontData(d => ({ ...d, announcement: e.target.value }))}
                      className="w-full px-4 py-3 rounded-lg border focus:outline-none focus:ring-2"
                      style={{ borderColor: 'var(--border-light)' }}
                      placeholder="Ej: Envio gratis en compras mayores a $20!"
                      data-testid="announcement-input" />
                    <p className="text-xs text-text-muted mt-1">Aparece como barra destacada arriba de la pagina. Dejar vacio para no mostrar.</p>
                  </div>

                  {/* Social & WhatsApp */}
                  <div className="bg-white rounded-xl border p-4 md:p-6" style={{ borderColor: 'var(--border-light)' }}>
                    <h3 className="font-bold mb-4 flex items-center gap-2">
                      <Phone size={20} className="text-accent-text" />
                      Redes Sociales y WhatsApp
                    </h3>
                    <div className="space-y-3">
                      <div>
                        <label className="block text-sm font-medium mb-1">WhatsApp (con codigo de pais)</label>
                        <input type="text" value={storefrontData.whatsapp}
                          onChange={(e) => setStorefrontData(d => ({ ...d, whatsapp: e.target.value }))}
                          className="w-full px-4 py-3 rounded-lg border focus:outline-none focus:ring-2"
                          style={{ borderColor: 'var(--border-light)' }}
                          placeholder="593991234567"
                          data-testid="whatsapp-input" />
                        <p className="text-xs text-text-muted mt-1">Muestra boton flotante de WhatsApp en la tienda</p>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-sm font-medium mb-1">Instagram</label>
                          <input type="text" value={storefrontData.instagram}
                            onChange={(e) => setStorefrontData(d => ({ ...d, instagram: e.target.value }))}
                            className="w-full px-4 py-3 rounded-lg border focus:outline-none focus:ring-2"
                            style={{ borderColor: 'var(--border-light)' }}
                            placeholder="@mitienda"
                            data-testid="instagram-input" />
                        </div>
                        <div>
                          <label className="block text-sm font-medium mb-1">Facebook</label>
                          <input type="text" value={storefrontData.facebook}
                            onChange={(e) => setStorefrontData(d => ({ ...d, facebook: e.target.value }))}
                            className="w-full px-4 py-3 rounded-lg border focus:outline-none focus:ring-2"
                            style={{ borderColor: 'var(--border-light)' }}
                            placeholder="mitienda"
                            data-testid="facebook-input" />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Show Prices + Layout */}
                  <div className="bg-white rounded-xl border p-4 md:p-6" style={{ borderColor: 'var(--border-light)' }}>
                    <h3 className="font-bold mb-4 flex items-center gap-2">
                      <Settings size={20} className="text-accent-text" />
                      Opciones de Vista
                    </h3>
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium text-sm">Mostrar precios</p>
                          <p className="text-xs text-text-muted">Si lo desactivas, muestra "Consultar precio"</p>
                        </div>
                        <button type="button" onClick={() => setStorefrontData(d => ({ ...d, show_prices: !d.show_prices }))}
                          className="w-12 h-6 rounded-full transition-colors relative"
                          style={{ background: storefrontData.show_prices ? 'var(--accent-primary)' : '#d1d5db' }}
                          data-testid="show-prices-toggle">
                          <div className="w-5 h-5 bg-white rounded-full absolute top-0.5 transition-all shadow"
                            style={{ left: storefrontData.show_prices ? '26px' : '2px' }} />
                        </button>
                      </div>
                      <div>
                        <p className="font-medium text-sm mb-2">Vista por defecto</p>
                        <div className="grid grid-cols-2 gap-2">
                          {[{ value: 'grid', label: 'Cuadricula' }, { value: 'list', label: 'Lista' }].map(v => (
                            <button key={v.value} type="button"
                              onClick={() => setStorefrontData(d => ({ ...d, layout_style: v.value }))}
                              className="py-3 px-3 rounded-lg border text-center transition-all text-sm"
                              style={{
                                borderColor: storefrontData.layout_style === v.value ? 'var(--accent-primary)' : 'var(--border-light)',
                                background: storefrontData.layout_style === v.value ? 'var(--accent-wash)' : 'white',
                                fontWeight: storefrontData.layout_style === v.value ? 600 : 400
                              }}
                              data-testid={`layout-${v.value}`}>
                              {v.label}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Product Card Size */}
                  <div className="bg-white rounded-xl border p-4 md:p-6" style={{ borderColor: 'var(--border-light)' }}>
                    <h3 className="font-bold mb-4 flex items-center gap-2">
                      <Package size={20} className="text-accent-text" />
                      Tamano de Productos
                    </h3>
                    <div className="grid grid-cols-3 gap-2">
                      {[
                        { value: 'small', label: 'Pequeno' },
                        { value: 'medium', label: 'Mediano' },
                        { value: 'large', label: 'Grande' }
                      ].map(s => (
                        <button key={s.value} type="button"
                          onClick={() => setStorefrontData(d => ({ ...d, product_card_size: s.value }))}
                          className="py-3 px-3 rounded-lg border text-center transition-all text-sm"
                          style={{
                            borderColor: storefrontData.product_card_size === s.value ? 'var(--accent-primary)' : 'var(--border-light)',
                            background: storefrontData.product_card_size === s.value ? 'var(--accent-wash)' : 'white',
                            fontWeight: storefrontData.product_card_size === s.value ? 600 : 400
                          }}
                          data-testid={`card-size-${s.value}`}>
                          {s.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Featured Products */}
                  <div className="bg-white rounded-xl border p-4 md:p-6" style={{ borderColor: 'var(--border-light)' }}>
                    <h3 className="font-bold mb-2 flex items-center gap-2">
                      <Star size={20} className="text-yellow-500" />
                      Productos Destacados
                    </h3>
                    <p className="text-xs text-text-muted mb-4">Selecciona los productos que quieres mostrar primero como destacados</p>
                    <div className="space-y-2 max-h-60 overflow-y-auto">
                      {storeProducts.map(p => {
                        const isFeatured = storefrontData.featured_products.includes(p.id);
                        return (
                          <button key={p.id} type="button"
                            onClick={() => {
                              setStorefrontData(d => ({
                                ...d,
                                featured_products: isFeatured
                                  ? d.featured_products.filter(id => id !== p.id)
                                  : [...d.featured_products, p.id]
                              }));
                            }}
                            className="w-full flex items-center gap-3 p-2 rounded-lg border transition-all text-left"
                            style={{
                              borderColor: isFeatured ? '#f59e0b' : 'var(--border-light)',
                              background: isFeatured ? '#fffbeb' : 'white'
                            }}
                            data-testid={`featured-toggle-${p.id}`}>
                            <img src={p.image} alt="" className="w-10 h-10 rounded-lg object-cover flex-shrink-0" />
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-sm truncate">{p.name}</p>
                              <p className="text-xs text-text-muted">${p.price?.toFixed(2)}</p>
                            </div>
                            {isFeatured && <Star size={18} className="text-yellow-500 flex-shrink-0" fill="#f59e0b" />}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Product Order */}
                  <div className="bg-white rounded-xl border p-4 md:p-6" style={{ borderColor: 'var(--border-light)' }}>
                    <h3 className="font-bold mb-2 flex items-center gap-2">
                      <GripVertical size={20} className="text-accent-text" />
                      Orden de Productos
                    </h3>
                    <p className="text-xs text-text-muted mb-4">Usa las flechas para reordenar. Los productos destacados siempre aparecen primero.</p>
                    <div className="space-y-1 max-h-80 overflow-y-auto">
                      {(() => {
                        const ordered = storefrontData.product_order.length > 0
                          ? [...storeProducts].sort((a, b) => {
                              const iA = storefrontData.product_order.indexOf(a.id);
                              const iB = storefrontData.product_order.indexOf(b.id);
                              if (iA === -1 && iB === -1) return 0;
                              if (iA === -1) return 1;
                              if (iB === -1) return -1;
                              return iA - iB;
                            })
                          : storeProducts;
                        return ordered.map((p, idx) => (
                          <div key={p.id} className="flex items-center gap-2 p-2 rounded-lg border"
                            style={{ borderColor: 'var(--border-light)' }}>
                            <span className="text-xs text-text-muted w-6 text-center font-bold">{idx + 1}</span>
                            <img src={p.image} alt="" className="w-8 h-8 rounded object-cover flex-shrink-0" />
                            <p className="flex-1 text-sm font-medium truncate">{p.name}</p>
                            <div className="flex gap-1 flex-shrink-0">
                              <button type="button" disabled={idx === 0}
                                onClick={() => {
                                  const ids = ordered.map(x => x.id);
                                  [ids[idx - 1], ids[idx]] = [ids[idx], ids[idx - 1]];
                                  setStorefrontData(d => ({ ...d, product_order: ids }));
                                }}
                                className="p-1 rounded hover:bg-gray-100 disabled:opacity-30">
                                <ArrowUp size={14} />
                              </button>
                              <button type="button" disabled={idx === ordered.length - 1}
                                onClick={() => {
                                  const ids = ordered.map(x => x.id);
                                  [ids[idx], ids[idx + 1]] = [ids[idx + 1], ids[idx]];
                                  setStorefrontData(d => ({ ...d, product_order: ids }));
                                }}
                                className="p-1 rounded hover:bg-gray-100 disabled:opacity-30">
                                <ArrowDown size={14} />
                              </button>
                            </div>
                          </div>
                        ));
                      })()}
                    </div>
                  </div>

                  {/* Save Button */}
                  <button type="button" disabled={savingStorefront}
                    onClick={async () => {
                      setSavingStorefront(true);
                      try {
                        // Upload banner if selected
                        if (bannerFile) {
                          const formData = new FormData();
                          formData.append('file', bannerFile);
                          const bannerRes = await axios.post(`${API}/admin/store-banner`, formData, {
                            headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' }
                          });
                          storefrontData.banner_url = bannerRes.data.banner_url;
                        }
                        // Save storefront data
                        await axios.put(`${API}/admin/store-storefront`, {
                          welcome_text: storefrontData.welcome_text,
                          featured_products: storefrontData.featured_products,
                          product_order: storefrontData.product_order,
                          product_card_size: storefrontData.product_card_size,
                          store_subtitle: storefrontData.store_subtitle,
                          announcement: storefrontData.announcement,
                          whatsapp: storefrontData.whatsapp,
                          instagram: storefrontData.instagram,
                          facebook: storefrontData.facebook,
                          show_prices: storefrontData.show_prices,
                          layout_style: storefrontData.layout_style
                        }, { headers: getAuthHeader() });
                        toast.success('Tienda virtual actualizada');
                        setBannerFile(null);
                      } catch (error) {
                        toast.error(error.response?.data?.detail || 'Error al guardar');
                      } finally {
                        setSavingStorefront(false);
                      }
                    }}
                    className="btn-primary w-full" data-testid="save-storefront-btn">
                    <Save size={20} className="mr-2" />
                    {savingStorefront ? 'Guardando...' : 'Guardar Tienda Virtual'}
                  </button>
                </>
              )}
            </div>
          )}

          {/* Billing/Facturación Tab */}
          {activeTab === 'billing' && (
            <div className="space-y-6">
              <div className="bg-white rounded-xl border p-4 md:p-6 space-y-5" style={{ borderColor: 'var(--border-light)' }}>
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 rounded-lg bg-green-50 flex items-center justify-center"><Zap size={20} className="text-green-600" /></div>
                  <div><h3 className="font-bold">Facturación Electrónica</h3><p className="text-xs text-text-muted">Integración con Factuplan - SRI Ecuador</p></div>
                  {settings.factuplan_api_key && (
                    <span className={`ml-auto text-xs font-bold px-3 py-1 rounded-full ${settings.factuplan_api_key.startsWith('ak_test_') ? 'bg-amber-100 text-amber-700' : settings.factuplan_api_key.startsWith('ak_live_') ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                      {settings.factuplan_api_key.startsWith('ak_test_') ? 'MODO PRUEBAS' : settings.factuplan_api_key.startsWith('ak_live_') ? 'PRODUCCIÓN' : 'ENTORNO DESCONOCIDO'}
                    </span>
                  )}
                </div>
                {settings.factuplan_api_key?.startsWith('ak_test_') && (
                  <div className="p-3 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-800">
                    <strong>Modo pruebas:</strong> Los comprobantes de prueba se eliminan cada hora en Factuplan. Es normal que no aparezcan luego en el historial.
                  </div>
                )}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium mb-2">Régimen de IVA</label>
                    <div className="grid grid-cols-2 gap-3">
                      <button type="button" onClick={() => setSettings({...settings, iva_regime: 'iva_15'})}
                        className="py-3 px-4 rounded-lg border-2 text-center transition-all text-sm font-medium"
                        style={{ borderColor: settings.iva_regime === 'iva_15' ? 'var(--accent-primary)' : 'var(--border-light)', background: settings.iva_regime === 'iva_15' ? 'var(--accent-wash)' : 'white', color: settings.iva_regime === 'iva_15' ? 'var(--accent-text)' : 'var(--text-primary)' }}
                        data-testid="iva-regime-15">
                        Con IVA (15%)
                      </button>
                      <button type="button" onClick={() => setSettings({...settings, iva_regime: 'iva_0'})}
                        className="py-3 px-4 rounded-lg border-2 text-center transition-all text-sm font-medium"
                        style={{ borderColor: (!settings.iva_regime || settings.iva_regime === 'iva_0') ? 'var(--accent-primary)' : 'var(--border-light)', background: (!settings.iva_regime || settings.iva_regime === 'iva_0') ? 'var(--accent-wash)' : 'white', color: (!settings.iva_regime || settings.iva_regime === 'iva_0') ? 'var(--accent-text)' : 'var(--text-primary)' }}
                        data-testid="iva-regime-0">
                        Sin IVA
                      </button>
                    </div>
                    <p className="text-xs text-text-muted mt-1">Se aplica automáticamente a todos los productos en cada factura.</p>
                  </div>
                  <div><label className="block text-sm font-medium mb-1">API Token de Factuplan</label>
                    <input type="text" value={settings.factuplan_api_key || ''} onChange={e => setSettings({...settings, factuplan_api_key: e.target.value})}
                      className="w-full px-4 py-3 rounded-lg border text-sm" style={{ borderColor: 'var(--border-light)' }} placeholder="ak_test_... o ak_live_..." data-testid="factuplan-api-key" /></div>
                  <div><label className="block text-sm font-medium mb-1">RUC del Emisor (13 dígitos)</label>
                    <input type="text" value={settings.factuplan_ruc || ''} maxLength={13} onChange={e => setSettings({...settings, factuplan_ruc: e.target.value})}
                      className="w-full px-4 py-3 rounded-lg border text-sm" style={{ borderColor: 'var(--border-light)' }} placeholder="0990000000001" data-testid="factuplan-ruc" /></div>
                  <div><label className="block text-sm font-medium mb-1">Razón Social</label>
                    <input type="text" value={settings.factuplan_business_name || ''} onChange={e => setSettings({...settings, factuplan_business_name: e.target.value})}
                      className="w-full px-4 py-3 rounded-lg border text-sm" style={{ borderColor: 'var(--border-light)' }} placeholder="Mi Empresa S.A." /></div>
                  <div><label className="block text-sm font-medium mb-1">Dirección Establecimiento</label>
                    <input type="text" value={settings.factuplan_business_address || ''} onChange={e => setSettings({...settings, factuplan_business_address: e.target.value})}
                      className="w-full px-4 py-3 rounded-lg border text-sm" style={{ borderColor: 'var(--border-light)' }} placeholder="Av. Principal y Calle" /></div>
                  <div><label className="block text-sm font-medium mb-1">Establecimiento</label>
                    <input type="text" value={settings.factuplan_establishment || '001'} maxLength={3} onChange={e => setSettings({...settings, factuplan_establishment: e.target.value})}
                      className="w-full px-4 py-3 rounded-lg border text-sm" style={{ borderColor: 'var(--border-light)' }} placeholder="001" /></div>
                  <div><label className="block text-sm font-medium mb-1">Punto de Emisión</label>
                    <input type="text" value={settings.factuplan_emission_point || '001'} maxLength={3} onChange={e => setSettings({...settings, factuplan_emission_point: e.target.value})}
                      className="w-full px-4 py-3 rounded-lg border text-sm" style={{ borderColor: 'var(--border-light)' }} placeholder="001" /></div>
                </div>
                <div className="flex gap-3">
                  <button type="button" onClick={async () => {
                    try { await axios.put(`${API}/settings`, settings, { headers: getAuthHeader() }); toast.success('Guardado');
                      const res = await axios.post(`${API}/factuplan/test`, {}, { headers: getAuthHeader() });
                      res.data.success ? toast.success(res.data.message) : toast.error(res.data.message);
                    } catch (e) { toast.error('Error al probar'); }
                  }} className="px-4 py-2 rounded-lg border text-sm font-medium" style={{ borderColor: 'var(--accent-primary)', color: 'var(--accent-text)' }} data-testid="test-factuplan-btn">
                    <Zap size={16} className="inline mr-1" /> Probar Conexión</button>
                  <button type="button" onClick={async () => {
                    try { await axios.put(`${API}/settings`, settings, { headers: getAuthHeader() }); toast.success('Facturación guardada'); } catch { toast.error('Error'); }
                  }} className="btn-primary text-sm"><Save size={16} className="mr-1" /> Guardar</button>
                </div>
              </div>
              <div className="p-3 rounded-lg bg-blue-50 border border-blue-200 text-xs text-blue-800">
                <strong>Envío de correo:</strong> Factuplan envía automáticamente el PDF y XML de la factura al correo del cliente. No necesitas configurar servidores de correo propios.
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default StoreSettings;
